import { DrawerCloseIcon } from "hec/assets/icons";
import { memo } from "react";
import { toast } from "react-hot-toast";

import type { ValidationErrorResponse } from "~/components/forms/validationErrorResponse.server";

import { isValidationError } from "~/components/forms/validationErrorResponse";
import { Button } from "~/components/ui/button";
import { useAuth } from "~/hec/context/auth-context";
import { SFL_UPDATE_FAILED } from "~/lib/constants";
import { cn } from "~/lib/ui";

import { useBasketActions } from "../hooks/basket-hooks";
import {
  useSaveForLater,
  useSaveForLaterActions,
} from "../hooks/save-for-later-hooks";
import { useOnFetcherSettled } from "../hooks/use-fetcher-settled";
import { useBasketRows } from "./basket-rows-context";
import type { SnapEntry } from "./basket-rows-derive";

type Props = {
  snap: SnapEntry;
  className?: string;
};

export const RemovedItemCard = memo(function RemovedItemCard({
  snap,
  className,
}: Props) {
  const ctx = useBasketRows();
  const sfl = useSaveForLater();
  const sflActions = useSaveForLaterActions();
  const { add, fetcher } = useBasketActions();

  const { lineItem, source } = snap;
  const itemId = lineItem.itemId;
  const productId = lineItem.productId;
  // Auth from AuthProvider, not the SFL fetch (COM-63 — gated to /cart).
  const isGuest = !useAuth().isLoggedIn;

  // Settle observer for the card's `add` submission. Re-add and SFL move
  // share the same basket `add` path; card re-add suppresses the ATC modal and
  // the restored row is the feedback. After a successful SFL move, chain SFL cleanup:
  // localStorage `guestRemove` for guests; `cleanupAfterMove` (a SFL remove
  // submitted under a dedicated key) for auth users — keyed separately so
  // the cart-page `sfl-remove` toast observer doesn't fire on cleanup.
  useOnFetcherSettled(fetcher, data => {
    if (!itemId) return;
    if (isValidationError(data)) {
      const v = data as ValidationErrorResponse;
      const msg =
        v.globalErrors?.[0] ??
        Object.values(v.fieldErrors ?? {})[0] ??
        "Couldn't re-add item";
      ctx?.setSnapError(itemId, msg);
      return;
    }
    if (data && typeof data === "object" && "successfullyAdded" in data) {
      const replacementItemId = (
        data as {
          basket?: {
            productItems?: Array<{ itemId?: string; productId?: string }>;
          };
        }
      ).basket?.productItems?.find(
        item => item.productId === productId,
      )?.itemId;
      if (source === "savedForLater" && productId) {
        if (isGuest) {
          // COM-63 — guestRemove returns false when the localStorage write
          // fails; the item is already back in the cart, so a silently-ignored
          // failure would leave it in BOTH the cart and Saved-for-Later.
          // Surface it (mirrors the authed cleanup path's toast).
          if (sfl && sfl.guestRemove(productId) === false) {
            toast.error(SFL_UPDATE_FAILED);
          }
        } else {
          sfl?.optimisticRemove(productId);
          sflActions.cleanupAfterMove(productId);
        }
      }
      ctx?.restoreSnap(itemId, replacementItemId);
    }
  });

  const onAddBack = () => {
    if (!itemId || !productId) return;
    // ET-287: a telehealth line's intake reference has to come back with it —
    // the cart row withholds "Edit plan" from a line without one. The snap is
    // the only place it still exists once the line is gone; the action decides
    // whether to honor it (`shouldRestampAddBackQrid`).
    const questionnaireResponseId = lineItem.c_th_questionnaire_response_id;
    add(
      {
        productId,
        ...(questionnaireResponseId ? { questionnaireResponseId } : {}),
      },
      lineItem.quantity ?? 1,
      {
        source: "addBack",
        suppressAddedToBasketModal: true,
      },
    );
  };

  const inFlight = fetcher.state !== "idle";

  const error = itemId ? ctx?.snapErrors.get(itemId) : undefined;
  const productName = lineItem.productName ?? "";
  const headline =
    source === "savedForLater"
      ? `${productName} has been moved to Save for Later`
      : `You just removed ${productName} from your cart`;
  // XD-189: while dismissing, the provider flags this snap "leaving" so the card
  // collapses vertically (grid-rows 1fr→0fr) instead of vanishing. The provider
  // unmounts it once the COLLAPSE_MS transition has run.
  const isLeaving = itemId ? (ctx?.leaving.has(itemId) ?? false) : false;

  return (
    <li
      data-testid="minicart-removed-card"
      data-pid={productId}
      data-leaving={isLeaving ? "" : undefined}
      className={cn(
        "grid grid-rows-[1fr] transition-[grid-template-rows,opacity] duration-300 ease-out",
        "data-[leaving]:pointer-events-none data-[leaving]:grid-rows-[0fr] data-[leaving]:opacity-0",
        "motion-reduce:transition-none",
      )}
    >
      <div className="overflow-hidden">
        <div
          className={cn(
            "relative flex flex-col items-center gap-1 p-4 text-center",
            className,
          )}
        >
          <button
            type="button"
            aria-label={`Dismiss removed item ${productName}`}
            onClick={() => itemId && ctx?.dismissSnap(itemId)}
            className="right-2 flex h-6 w-6 items-center justify-center self-end rounded-full text-neutral-700 hover:bg-neutral-100"
            data-testid="minicart-removed-card-dismiss"
          >
            <DrawerCloseIcon className="h-4 w-4" aria-hidden="true" />
          </button>
          <p className="mt-3 font-grotesque text-lg font-medium text-contrast-black">
            {headline}
          </p>
          {error && (
            <p
              aria-live="polite"
              className="font-grotesque text-xs text-red-11"
            >
              {error}
            </p>
          )}
          <Button
            variant="cta"
            size="5"
            onClick={onAddBack}
            disabled={inFlight}
            className="mb-12 mt-4"
          >
            Add Item Back<span className="sr-only"> — {productName}</span>
          </Button>
        </div>
      </div>
    </li>
  );
});
RemovedItemCard.displayName = "RemovedItemCard";
